import React, { useState, useEffect, useRef } from 'react'
import {
    useParams,
    useHistory,
    Link,
    matchPath,
    useLocation,
    Route,
    Switch,
    useRouteMatch,
} from 'react-router-dom'

import axios from 'axios'
import moment from 'moment'
import { useReactOidc } from '@axa-fr/react-oidc-context'

import config from '../../config/index'
import { getFullUrl } from '../../infrastructure/utils'
import { FETCH_STATUS } from '../../infrastructure/fetch/constants'
import { Default, Desktop, Tablet, Mobile } from '../../utils/responsive'
import Spinner from '../../components/Spinner'
import Subnav from '../../components/Subnav'
import Error from '../../components/Error'
import Header from '../Header'

import Audience from './audience/Audience'
import Visits from './visits/Visits'
import Sales from './sales/Sales'
import Scans from './scans/Scans'
import TicketsIssued from './ticketsIssued/TicketsIssued'
import Impressions from './impressions/Impressions'

const navigationItems = (event, location, oidcProfile) =>
    [
        {
            title: 'Sales',
            icon: 'money',
            link: `/events/${event.eventId}/sales`,
            items: [],
            show: true,
        },
        {
            title: 'Visits',
            icon: 'th-large',
            link: `/events/${event.eventId}/visits`,
            items: [],
            show: true,
        },
        // {
        //     title: 'Impressions',
        //     icon: 'eye',
        //     link: `/events/${event.eventId}/Impressions`,
        //     items: [],
        //     show: true,
        // },
        {
            title: 'Demographics',
            icon: 'users',
            link: `/events/${event.eventId}/audience`,
            items: [],
            show: true,
        },
        {
            title: 'Scans',
            icon: 'barcode',
            link: `/events/${event.eventId}/scans`,
            items: [],
            show: true
        },
        {
            title: 'Tickets Issued',
            icon: 'ticket',
            link: `/events/${event.eventId}/tickets-issued`,
            items: [],
            show: true,
        },
    ]
        .map(item => {
            let isActive = matchPath(location.pathname, { path: item.link })
            if (isActive == null) {
                isActive = matchPath(location.pathname, {
                    path: item.link.replace('/sales', ''),
                    exact: true,
                })
            }

            return {
                ...item,
                isActive: isActive != null,
            }
        })
        .filter(item => item.show)

const EventSearchBox = () => {
    const history = useHistory()
    const input = useRef(null)

    return (
        <div className="sk-search-box">
            <form
                onSubmit={() =>
                    history.push(`/events?q=${input.current.value}`)
                }
            >
                <div className="sk-search-box__icon" />
                <input
                    type="text"
                    data-qa="query"
                    className="sk-search-box__text"
                    placeholder="Search"
                    ref={input}
                />
                <input
                    type="submit"
                    value="search"
                    className="sk-search-box__action"
                    data-qa="submit"
                />
                <div
                    data-qa="loader"
                    className="sk-search-box__loader sk-spinning-loader is-hidden"
                />
            </form>
        </div>
    )
}
const EventProfileLoading = () => (
    <div className="dropdown profile-element">
        <span>
            <div
                className="blank-placeholder"
                style={{ width: 170, height: 60 }}
            />
        </span>
        <span className="block m-t-xs">
            <div className="blank-placeholder" style={{ width: 60 }} />
            <div className="blank-placeholder" style={{ width: 100 }} />
        </span>
        <span className="text-muted text-xs block">
            <div className="blank-placeholder" style={{ width: 120 }} />
        </span>
        <span className="text-muted text-xs block">
            <div className="blank-placeholder" style={{ width: 40 }} />
        </span>
    </div>
)

const EventProfile = ({ event }) => (
    <div className="dropdown profile-element animated fadeIn">
        <div className="event-image-thumbnail">
            <img
                alt="Description"
                src={event.imageUrl}
                style={{
                    width: 170,
                    borderRadius: 4,
                }}
            />
        </div>
        <span className="block m-t-xs">
            <strong className="font-bold title">{event.name}</strong>
        </span>
        <span className="text-muted text-xs block">
            {moment(event.eventStartDate).format('DD MMM YYYY hh:mm A')}{' '}
            {event.venueTimezone}
            <br />
            {event.venueName}
        </span>
    </div>
)

const Navigation = ({ children }) => (
    <nav className="navbar-default navbar-static-side" role="navigation">
        {children}
    </nav>
)

const Page = ({ children }) => (
    <div id="page-wrapper" className="gray-bg">
        {children}
    </div>
)

const PageHeading = ({ title, children }) => (
    <div className="row wrapper border-bottom white-bg page-heading">
        <div className="col-lg-12">
            {title && <h2>{title}</h2>}
            {children}
        </div>
    </div>
)

const Failed = ({ retry }) => (
    <div id="wrapper">
        <Header search={<EventSearchBox />} />
        <Navigation>
            <ul className="nav metismenu" id="side-menu">
                <li className="nav-header">
                    <EventProfileLoading />
                </li>
            </ul>
        </Navigation>
        <Page title="Events">
            <PageHeading>
                <div className="flex jc-space-between">
                    <div>
                        <h1>Oops, something went wrong</h1>
                        <ol className="breadcrumb">
                            <li>
                                <Link to="/">Home</Link>
                            </li>
                            <li>
                                <Link to="/events">Events</Link>
                            </li>
                        </ol>
                    </div>
                </div>
            </PageHeading>
            <div className="wrapper wrapper-content">
                <Error retry={retry} message={'Could not load event'} />
            </div>
        </Page>
    </div>
)

const Loading = () => (
    <div id="wrapper">
        <Header search={<EventSearchBox />} />
        <Navigation>
            <ul className="nav metismenu" id="side-menu">
                <li className="nav-header">
                    <EventProfileLoading />
                </li>
            </ul>
        </Navigation>
        <Page title="Events">
            <PageHeading>
                <div className="flex jc-space-between">
                    <div>
                        <h1>Event loading...</h1>
                        <Default>
                            <ol className="breadcrumb hide-mobile">
                                <li>
                                    <Link to="/">Home</Link>
                                </li>
                                <li>
                                    <Link to="/events">Events</Link>
                                </li>
                            </ol>
                        </Default>
                    </div>
                </div>
            </PageHeading>
            <div className="wrapper wrapper-content">
                <Spinner />
            </div>
        </Page>
    </div>
)

const Success = ({ event }) => {
    let location = useLocation()
    let { path } = useRouteMatch()
    const {
        oidcUser: { profile },
    } = useReactOidc()

    const navigation = navigationItems(event, location, profile)

    return (
        <div id="wrapper">
            <Header search={<EventSearchBox />} />
            <Navigation>
                <ul className="nav metismenu" id="side-menu">
                    <li className="nav-header">
                        <EventProfile event={event} />
                    </li>

                    {navigation.map(item => (
                        <li
                            key={item.title}
                            className={item.isActive ? 'active' : undefined}
                        >
                            <Link to={item.link}>
                                <i className={`fa fa-${item.icon}`} />
                                <span className="nav-label">{item.title}</span>
                            </Link>
                        </li>
                    ))}
                </ul>
            </Navigation>
            <Page title="Events">
                <PageHeading>
                    <div className="flex jc-space-between">
                        <div>
                            <h1>{event.name}</h1>
                            <Desktop>
                                <ol className="breadcrumb">
                                    <li>
                                        <Link to="/">Home</Link>
                                    </li>
                                    <li>
                                        <Link to="/events">Events</Link>
                                    </li>
                                    <li>
                                        <Link to={`/events/${event.eventId}`}>
                                            {event.name}
                                        </Link>
                                    </li>
                                </ol>
                            </Desktop>
                        </div>
                        <div style={{ float: 'right' }}>
                            <a
                                href={
                                    config.ENVIRONMENT_TYPE === 'Production'
                                        ? `https://studio.oztix.com.au/events/${event.eventId}`
                                        : `https://development-studio.oztix.com.au/events/${event.eventId}`
                                }
                                className="btn btn-primary"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Studio
                            </a>
                        </div>
                    </div>
                    <Tablet>
                        <Subnav items={navigation} />
                    </Tablet>
                    <Mobile>
                        <Subnav items={navigation} />
                    </Mobile>
                </PageHeading>
                <Switch>
                    <Route exact path={path}>
                        <Sales event={event} />
                    </Route>
                    <Route path={`${path}/sales`}>
                        <Sales event={event} />
                    </Route>
                    <Route path={`${path}/visits`}>
                        <Visits event={event} />
                    </Route>
                    <Route path={`${path}/impressions`}>
                        <Impressions event={event} />
                    </Route>
                    <Route path={`${path}/audience`}>
                        <Audience />
                    </Route>
                    <Route path={`${path}/scans`}>
                        <Scans event={event} />
                    </Route>
                    <Route path={`${path}/tickets-issued`}>
                        <TicketsIssued event={event} />
                    </Route>
                </Switch>
            </Page>
        </div>
    )
}

const LayoutEntry = () => {
    const { id: eventId } = useParams()
    const [status, setStatus] = useState(FETCH_STATUS.NOT_STARTED)
    const [event, setEvent] = useState(null)
    const [randomValue, setRandomValue] = useState(() => Math.random())

    useEffect(() => {
        setStatus(FETCH_STATUS.REQUESTED)

        axios
            .get(getFullUrl(`/api/events/get-details/${eventId}`))
            .then(result => {
                setEvent(result.data)
                setStatus(FETCH_STATUS.SUCCEEDED)
            })
            .catch(() => setStatus(FETCH_STATUS.FAILED))
    }, [eventId, randomValue])

    const retry = () => setRandomValue(Math.random())

    return {
        [FETCH_STATUS.FAILED]: <Failed retry={retry} />,
        [FETCH_STATUS.NOT_STARTED]: <Loading />,
        [FETCH_STATUS.REQUESTED]: <Loading />,
        [FETCH_STATUS.SUCCEEDED]: <Success event={event} />,
    }[status]
}

export default LayoutEntry
